import {createSlice, createAsyncThunk, createAction} from "@reduxjs/toolkit";
import networkHandler from "../../network/networkHandler";
import {EApiMiddlewareMethods} from "../../network/networkHandler.types";
import {ExpressDashboardState} from "./expressSlice.types";
import AppConstants from "../../constants";
import moment from "moment";
import {transformSelectValues, saveFile, saveORExportFile} from "../../utils/helpers.utils";
import {transformBundleData} from "../../mocks/routePlanning/response.transforms";
import {getCrateDetails} from '../../utils/business/crate'
import customNetworHandler from "network/customNetworHandler";
import { apiKey, cWingsServiceBaseURL, expressDriverBaseURL } from "config/Api.config";
import { addNRPageAction } from "utils/newRelicUtils";
import { expressBaseURL } from "config/Api.config";
import { ASSIGNEE_TYPE } from "constants/ExpressConstants";
import { getCountryUrlByKey } from "utils/urlUtils";
import { COUNTRY_URL_KEYS, GEOFENCE_STATUS } from "constants/CommonConstants";
import { isBoolean } from "utils/lodash.utils";

export const fetchDynamicFiltersOrdersDropdownValues = createAsyncThunk(
    "Express/FetchDynamicFiltersOrdersDropdownValues",
    async ({
               filters
           }: {
               filters: any;
           },
           {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'order/dynamic-filter-count',
                method: EApiMiddlewareMethods.POST,
                data: filters
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isUAE: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchPlanningOrderListByFilter = createAsyncThunk(
    "Express/FetchOrdersByFilter",
    async (
        {
            filters
        }: {
            filters: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'order/filter',
                method: EApiMiddlewareMethods.POST,
                data: filters
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchOrdersFilterChipsDisplayCount = createAsyncThunk(
    "Express/FetchOrdersFilterChipsDisplayCount",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'order/displayFilterCount',
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchConsignmentHistory = createAsyncThunk(
    "ExpressOrderDetails/FetchConsignmentHistory",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `status/getOrderStatuses?type=ORDER&sort=DESC&orderBy=updatedAt&consignmentCode=${params}`,
                method: EApiMiddlewareMethods.GET,
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchStatusOnClick = createAsyncThunk(
    "ConsignmentDetails/FetchStatusOnClick",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `delayedUpdates/fetch/snapshot/${params}`,
                method: EApiMiddlewareMethods.GET,
            } as any;
            const {data} = await networkHandler(request, { isCamsService: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getAddressStatus = createAsyncThunk(
    "ExpressOrderDetails/GetAddressStatus",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `order/address-status`,
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchOrderLogs = createAsyncThunk(
    "ExpressOrderDetails/FetchOrderLogs",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `log/getOrderLogs?type=ORDER&consignmentCode=${params}`,
                method: EApiMiddlewareMethods.GET,
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchDriversList = createAsyncThunk(
    "DriversAssignment/FetchDriversList",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        const { hubCode, activity } = payload;
        try {
            const request = {
                url: `v2/driver?deliveryType=EXPRESS&hub=${hubCode}${payload.supportDriver ? '&supportDriver=true' : ''}&activity=${activity}`,
                method: EApiMiddlewareMethods.GET,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true, isExpressDriver: true });
            if (payload.supportDriver === true) {
                data.isSupportDriver = true
            }
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchSupportDriverDetails= createAsyncThunk(
    "DriversAssignment/FetchSupportDriverDetails",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `driver/getDrivers`,
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true, isExpressDriver: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const releaseSupportDriver= createAsyncThunk(
    "DriversAssignment/ReleaseSupportDriver",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            addNRPageAction(AppConstants.NR_FEATURES.SUPPORT_DRIVER.NAME, AppConstants.NR_FEATURES.SUPPORT_DRIVER.FEATURES.RELEASE_SUPPORT_DRIVER);
            const request = {
                url: `order/release-driver/support`,
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const releasePrimaryDriver= createAsyncThunk(
    "DriversAssignment/ReleasePrimaryDriver",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            addNRPageAction(AppConstants.NR_FEATURES.PRIMARY_DRIVER.NAME, AppConstants.NR_FEATURES.PRIMARY_DRIVER.FEATURES.RELEASE_PRIMARY_DRIVER);
            const request = {
                url: `order/release-driver/primary`,
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const countryUrl = getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS);
            const url = countryUrl || expressBaseURL;
            const serviceType = countryUrl ? COUNTRY_URL_KEYS.EWINGS : undefined;
            const { data } = await customNetworHandler(request, url, serviceType);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);



export const assignDriverManually = createAsyncThunk(
    "DriverAssignment/AssignDriverManually",
    async (
        {
            payload,
            skipLoading
        }: {
            payload: any;
            skipLoading?: boolean;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `order/assign-driver`,
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return { data, skipLoading };
        } catch (error: any) {
            return rejectWithValue({ error: error.response.data, skipLoading });
        }
    }
);

export const assignSupportDriverManually= createAsyncThunk(
    "DriverAssignment/AssignSupportDriverManually",
    async (
        {
            payload
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            addNRPageAction(AppConstants.NR_FEATURES.SUPPORT_DRIVER.NAME, AppConstants.NR_FEATURES.SUPPORT_DRIVER.FEATURES.ASSIGN_SUPPORT_DRIVER);
            const request = {
                url: `/order/assign-driver/support`,
                method: EApiMiddlewareMethods.POST,
                data: payload
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const exportToExcel = createAsyncThunk(
    "Express/ExportOrderToExcel",
    async (
        {
            payload,
        }: {
            payload: object;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: '/fulfilment/v2/export/express/orders',
                method: EApiMiddlewareMethods.POST,
                data: payload,
                responseType: "blob"
            } as any;
            request.headers = {
                'generateResponse': false,
              }
              const response =  await customNetworHandler(request, cWingsServiceBaseURL);
              return response;
        } catch (error: any) {
            const errorResponse = await error?.response?.data?.text();
            const errorObj = JSON.parse(errorResponse);
            return rejectWithValue(errorObj?.error);
        }
    }
);

export const fetchDeliveryDriversByHub = createAsyncThunk(
    "Express/FetchDeliveryDriversByHub",
    async (
        {
            payload,
        }: {
            payload: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'assignee/filter',
                method: EApiMiddlewareMethods.POST,
                data: payload,
            } as any;
         
              const {data} =  await customNetworHandler(request, expressDriverBaseURL);
              return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const downloadOrderLogs = createAsyncThunk(
    "Express/ExportOrderLogs",
    async (
        {
            payload,
        }: {
            payload: object;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'order/export-logs',
                method: EApiMiddlewareMethods.POST,
                data: payload,
                responseType: "blob"
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const exportInvoicePdf = createAsyncThunk(
    "Express/ExportInvoicePdf",
    async (
        {
            params,
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: '/fulfilment/invoice/v3/view',
                method: EApiMiddlewareMethods.POST,
                data: params,
                responseType: "bytes"
            } as any;
            const {data} = await customNetworHandler(request, cWingsServiceBaseURL);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchExpressOrderDeliveryStatuses = createAsyncThunk(
    "Express/FetchExpressOrderDeliveryStatuses",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `order/statuses/${params}`,
                method: EApiMiddlewareMethods.GET,
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchDeliveryReasons = createAsyncThunk(
    "Express/FetchDeliveryReasons",
    async (
        {
            params,
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'reasons/get',
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchBundleProducts = createAsyncThunk(
    "Express/FetchBundleProducts",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `product/${params}`,
                method: EApiMiddlewareMethods.GET,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const expressOrderReplanning = createAsyncThunk(
    "Express/ExpressOrderReplanning",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'v3/order/replanning',
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const replanDuringPartialRejection = createAsyncThunk(
    "Express/ReplanAndPartialRejection",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = { 
                url: 'v3/order/replan-order-line-items',
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
)

export const submitOrderComments = createAsyncThunk(
    "Express/SubmitOrderComments",
    async (
        {
            params,
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: 'order/add-comment',
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchClubbingSuggestion = createAsyncThunk(
    "Express/FetchClubbingSuggestion",
    async (
        {
            params,
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
           const { hubCode, consignmentCode } = params;
        try {
            const request = {
                url: `clubbing/sequence?hubCode=${hubCode}&consignmentCode=${consignmentCode}` ,
                method: EApiMiddlewareMethods.GET,
                data: params
            } as any;
            const {data} = await networkHandler(request, { isAuth: true, isExpress: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchCarrierDropdown = createAsyncThunk(
    "ConsignmentDetails/FetchCarrierDropdown",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const queryParams: Record<string, string> = {};
            if (params.country) queryParams.country = params.country;
            if (params.deliveryType) queryParams.deliveryType = params.deliveryType;
            if (params.requestType) queryParams.requestType = params.requestType;
            if (params.sourceCourier) queryParams.sourceCourier = params.sourceCourier;
            
            const request = {
                url: 'carrier/list/suitable',
                method: EApiMiddlewareMethods.GET,
                params: queryParams
            } as any;
            request.headers = {
                'x-api-key':  apiKey,
                'client-id': 'courier-switch'
             };
            const {data} = await networkHandler(request, { isCamsService: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const switchCourier= createAsyncThunk(
    "Express/SwitchCourier",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {

            const request = {
                url: `v1/courier-switching/mark`,
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            const url = getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS) || expressBaseURL;
            const { data } = await customNetworHandler(request, url);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);


export const confirmCourierSwitch = createAsyncThunk(
    "ConsignmentDetails/confirmCourierSwitch",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {
            const request = {
                url: `v1/courier-switching`,
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            request.headers = {
                'x-api-key':  apiKey,
                'client-id': 'courier-switch'
             };
            const {data} = await networkHandler(request, { isCamsService: true });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const denyCourierSwitch = createAsyncThunk(
    "Express/DenyCourierSwitch",
    async (
        {
            params
        }: {
            params: any;
        },
        {rejectWithValue}
    ) => {
        try {

            const request = {
                url: `v1/courier-switching/unmark`,
                method: EApiMiddlewareMethods.POST,
                data: params
            } as any;
            request.headers = {
                Authorization:  sessionStorage.getItem("token"),
             };
            const url = getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS) || expressBaseURL;
            const { data } = await customNetworHandler(request, url);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);


export const clearClubbingSuggestion = createAction('express/clearClubbingSuggestion');

export const closeDialog = createAsyncThunk("Express/CloseDialog", () => {
    return false;
});
export const resetStatusRefreshButton = createAsyncThunk("Planning/resetStatusRefreshButton", () => {
    return false;
});
export const resetBundleList = createAsyncThunk("Planning/resetBundleList", () => {
    return false;
});

export const driverAssignmentSuccess = createAsyncThunk("Express/DriverAssignmentSuccess", () => {
    return false;
});

export const userLogout = createAsyncThunk("UserLogin/Logout", () => {
    return false;
});

export const resetExportInitiated = createAsyncThunk("Express/ResetExportInitiated", () => {  
    return false;
});

export const resetCourierState = createAsyncThunk("Express/ResetCourierState", () => {  
    return false;
});

const initialState: ExpressDashboardState = {
    loading: false,
    tableLoading: false,
    clubbingLoading: false,
    exportError: "",
    ordersFilterChipsDisplayCount: {
        consignmentCount: {
            UNASSIGNED: 0,
            ON_HOLD: 0,
            ASSIGNED: 0,
            OUT_FOR_DELIVERY: 0,
            COMPLETED: 0,
        }
    },
    ordersFiltersDropdowns: {
        hubCodeList: [],
        hubNameList: [],
        deliverySlotList: [],
        deliveryZonesList: [],
        addressStatusList: [],
        activityList:[],
        statusList: [],
        driverList: [],
        vehicleList: [],
        paymentTypeList: [],
        deliveryArea: [],
        orderStatusList: [],
        executionStatusList: [],
        supportDriverList: []
    },
    ordersData: {
        elements: [],
        pageNumber: 0
    },
    error: "",
    errorCode: "",
    isPolled: false,
    consignmentStatuses: [],
    orderLogs: [],
    driversList: [],
    supportDriverList: [],
    excelLoading: false,
    invoiceData: "",
    invoiceUrl: "",
    displayInvoice: false,
    invoiceMessage: "",
    invoiceConsignment: "",
    driverAssigned: false,
    supportDriverAssigned: false,
    orderDeliveryStatuses: [],
    reasonsDropdownList: [],
    orderCommentsList: [],
    deliveryReasons: {},
    rejectionReasons: {},
    orderCommentsReasons: {},
    podUploaded: null,
    geofenceLocation: null,
    consignmentDataAfterReplanning: {},
    replanningConsignmentSuccess: false,
    rejectionReasonList: [],
    productsAfterUpdateLineItem: [],
    bundleProductAfterUpdateLineItem: [],
    revisedAmountAfterCalculation: "",
    consignmentDataAfterAddressUpdate: {},
    isAddressUpdated: false,
    orderCommentsAfterSubmit: {},
    areOrderCommentsAdded: false,
    bundleProductList: [],
    productList: [],
    crateInfo: [],
    exportInitiated: false,
    reportId: "",
    supportDriverDetails: [],
    supportDriverUpdateResult: {},
    driverUpdateResult: {},
    primaryDriverUpdateResult: {},
    primaryDriverUnAssigned: false,
    driversListByHub: {
        elements: [],
        pageNumber: 0
    },
    helperListByHub:{
        elements: [],
        pageNumber: 0
    },
    carrierList: [],
    switchInfo: {},
    courierSwitchSuccess: false,
    courierSwitchCanceled: false,
    clubbingSuggestion: {
    }
};
const ADDRESS_STATUS_TEMP = [{code: "PENDING", value: "PENDING"}, {
    code: "UNVERIFIED",
    value: "UNVERIFIED"
}, {code: "VERIFIED", value: "VERIFIED"}]
const expressSlice = createSlice({
    name: "Express",
    initialState,
    reducers: {
        setClubbingAssignmentLoading: (state, action) => {
            state.clubbingLoading = action.payload;
        },
        resetDeliveryBadges: (state) => {
            state.podUploaded = null;
            state.geofenceLocation = null;
        },
    },
    extraReducers: (builders) => {
        builders
            .addCase(fetchOrdersFilterChipsDisplayCount.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchOrdersFilterChipsDisplayCount.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.replanningConsignmentSuccess = false;
                state.isAddressUpdated = false;
                state.ordersFilterChipsDisplayCount = payload && payload.results;
            })
            .addCase(fetchOrdersFilterChipsDisplayCount.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchPlanningOrderListByFilter.pending, (state) => {
                state.tableLoading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchPlanningOrderListByFilter.fulfilled, (state, action) => {
                const {
                    payload,
                    meta: {
                        arg: {filters}
                    }
                } = action;
                state.tableLoading = false;
                state.errorCode = "";
                state.error = "";
                state.replanningConsignmentSuccess = false;
                state.isAddressUpdated = false;
                state.ordersData = {
                    ...payload,
                    elements:
                        filters.pageNumber === 0
                            ? (payload && payload.results && Array.isArray(payload.results.elements) ? [...payload.results.elements] : [])
                            : [
                                ...(Array.isArray(state.ordersData.elements) ? state.ordersData.elements : []),
                                ...(payload && payload.results && Array.isArray(payload.results.elements) ? payload.results.elements : [])
                            ],
                    pageNumber: payload?.results?.pageNumber,
                    totalElements: payload?.results?.totalElements,
                    totalPages: payload?.results?.totalPages
                };
                if (payload && payload.results && payload.results.filters) {
                    state.ordersFiltersDropdowns = {
                        hubCodeList: transformSelectValues(payload.results.filters.hubCodes || []),
                        hubNameList: transformSelectValues(payload.results.filters.hubNames || []),
                        deliverySlotList: transformSelectValues(payload.results.filters.deliverySlots || []),
                        deliveryZonesList: transformSelectValues(payload.results.filters.deliveryZones || []),
                        statusList: transformSelectValues(payload.results.filters.statuses || []),
                        orderStatusList: transformSelectValues(payload.results.filters.orderStatuses || []),
                        driverList: transformSelectValues(payload.results.filters.drivers || []),
                        vehicleList: transformSelectValues(payload?.results?.filters?.orderVehicleTypes || []),
                        paymentTypeList: transformSelectValues(payload.results.filters.paymentTypes || []),
                        deliveryArea: transformSelectValues(payload.results.filters.areas || []),
                        addressStatusList: transformSelectValues(ADDRESS_STATUS_TEMP),
                        activityList: transformSelectValues(payload.results.filters.activities || []),
                        executionStatusList: transformSelectValues(payload.results.filters.deliveryExecutionStatus || []),
                        supportDriverList: transformSelectValues(payload.results.filters.supportDrivers || [])
                    };
                }
            })
            .addCase(fetchPlanningOrderListByFilter.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.tableLoading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchConsignmentHistory.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchConsignmentHistory.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.consignmentStatuses = payload.results.statuses || [];
                state.orderLogs = [];
            })
            .addCase(fetchConsignmentHistory.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchStatusOnClick.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchStatusOnClick.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.isPolled = payload.polled;
            })
            .addCase(fetchStatusOnClick.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchOrderLogs.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchOrderLogs.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.orderLogs = payload.results.logs || [];
            })
            .addCase(fetchOrderLogs.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchBundleProducts.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchBundleProducts.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.productList = payload?.results?.products
                state.bundleProductList = payload?.results?.bundleProducts?.length ? transformBundleData(payload?.results?.bundleProducts) : [];
                state.crateInfo = getCrateDetails(payload?.results)

            })
            .addCase(fetchBundleProducts.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(exportToExcel.pending, (state) => {
                state.excelLoading = true;
                state.error = "";
                state.exportInitiated = false;
                state.reportId = "";
            })
            .addCase(exportToExcel.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                const { data, headers } = action.payload;
                state.excelLoading = false;
                state.errorCode = "";
                state.error = "";
               if (payload) {
                 const { exportInitiated, requestId } = saveORExportFile(
                   data,
                   headers,
                   "Express_Orders"
                 );
                 state.exportInitiated = exportInitiated;
                 if (requestId) {
                   state.reportId = requestId;
                 }
               }  
            })
            .addCase(exportToExcel.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.excelLoading = false;
                state.exportInitiated = false;
                state.reportId = "";
                state.errorCode = errorPayload ? errorPayload?.errorCode : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload?.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(downloadOrderLogs.pending, (state) => {
                state.excelLoading = true;
                state.error = "";
            })
            .addCase(downloadOrderLogs.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.excelLoading = false;
                state.errorCode = "";
                state.error = "";
                if (payload) {
                    const currentDate = moment().format("YYYY-MM-DD");
                    const currentTime = moment().format("HH-mm");
                    const filename = `Order_Logs_${currentDate}_${currentTime}.xlsx`;
                    saveFile(payload, filename);
                }
            })
            .addCase(downloadOrderLogs.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.excelLoading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(exportInvoicePdf.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.invoiceData = "";
                state.displayInvoice = false;
            })
            .addCase(exportInvoicePdf.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                const data = payload?.data
                    if(data){
                        state.displayInvoice = true;
                        const pdfData = `data:application/pdf;base64,${data}`;
                        state.invoiceData = pdfData ;
                    }
            })
            .addCase(exportInvoicePdf.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.invoiceData = "";
                state.displayInvoice = false;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchDriversList.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.driversList = [];
                state.supportDriverList = [];
            })
            .addCase(fetchDriversList.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.driversList = payload.results || [];
                state.driverAssigned = false;
                if(payload.isSupportDriver){
                  state.supportDriverList = payload.results || [];
                }
                
            })
            .addCase(fetchDriversList.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchSupportDriverDetails.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchSupportDriverDetails.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.supportDriverDetails = payload.results || [];
            })
            .addCase(fetchSupportDriverDetails.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(releaseSupportDriver.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.supportDriverUpdateResult = {}
            })
            .addCase(releaseSupportDriver.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.supportDriverUpdateResult = payload?.results
                state.supportDriverAssigned = payload?.results && true; 
            })
            .addCase(releaseSupportDriver.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(releasePrimaryDriver.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.primaryDriverUpdateResult = {}
                state.primaryDriverUnAssigned = false;
            })
            .addCase(releasePrimaryDriver.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.primaryDriverUpdateResult = payload?.results
                state.primaryDriverUnAssigned = payload?.results && true; 
            })
            .addCase(releasePrimaryDriver.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            
            .addCase(assignDriverManually.pending, (state, action) => {
                const skipLoading = action.meta.arg.skipLoading;
                if (!skipLoading) {
                    state.loading = true;
                }
                state.errorCode = "";
                state.error = "";
            })
            .addCase(assignDriverManually.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                const skipLoading = action.payload?.skipLoading;
                if (!skipLoading) {
                    state.loading = false;
                }
                state.errorCode = "";
                state.error = "";
                state.driverAssigned = payload?.data && payload.data.status === "SUCCESS";
                state.driverUpdateResult = payload?.data?.results
            })
            .addCase(assignDriverManually.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                const skipLoading = errorPayload?.skipLoading;
                if (!skipLoading) {
                    state.loading = false;
                }
                state.errorCode = errorPayload ? errorPayload.error.error.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.error.error.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(assignSupportDriverManually.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(assignSupportDriverManually.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.supportDriverUpdateResult = payload?.results
                state.supportDriverAssigned = payload?.results && payload.status === "SUCCESS"; 
            })
            .addCase(assignSupportDriverManually.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.error.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            
            .addCase(fetchExpressOrderDeliveryStatuses.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchExpressOrderDeliveryStatuses.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.orderDeliveryStatuses = transformSelectValues(payload.results && payload.results.length ? payload.results : []);
            })
            .addCase(fetchExpressOrderDeliveryStatuses.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchDeliveryReasons.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchDeliveryReasons.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                let deliveryReasonsList: any = [];
                let rejectionReasonsList: any = [];
                let orderCommentsList: any = [];
                if (isBoolean(payload.results?.podUploaded)) {
                  state.podUploaded = payload.results.podUploaded;
                }
                if (payload.results?.geofenceLocation && Object.values(GEOFENCE_STATUS).includes(payload.results.geofenceLocation)) {
                  state.geofenceLocation = payload.results.geofenceLocation;
                }
                if (payload.results && payload.results.type === AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.LINE_ITEM_REJECTION) {
                    if (payload.results && payload.results.reasons) {
                        rejectionReasonsList = payload.results.reasons.map((reason: any) => {
                            return {
                                code: reason.reasonCode,
                                value: reason.reasonDescription
                            };
                        });
                    }
                    state.loading = false;
                    state.errorCode = "";
                    state.error = "";
                    state.rejectionReasonList = transformSelectValues(rejectionReasonsList);
                    state.rejectionReasons = payload.results.reasons;
                } else {
                    if (payload.results && payload.results.reasons) {
                        deliveryReasonsList = payload.results.reasons.map((reason: any) => {
                            return {
                                code: reason.reasonCode,
                                value: reason.reasonDescription
                            };
                        });
                    }
                    state.loading = false;
                    state.errorCode = "";
                    state.error = "";
                    state.reasonsDropdownList = transformSelectValues(deliveryReasonsList);
                    state.deliveryReasons = payload.results.reasons;
                }
                if (payload.results && payload.results.type === AppConstants.EXPRESS_CONSTANTS.DELIVERY_TYPE.ORDER_COMMENTS) {
                    if (payload.results && payload.results.reasons) {
                        orderCommentsList = payload.results.reasons.map((reason: any) => {
                            return {
                                code: reason.reasonCode,
                                value: reason.reasonDescription
                            };
                        });
                    }
                    state.loading = false;
                    state.errorCode = "";
                    state.error = "";
                    state.orderCommentsList = transformSelectValues(orderCommentsList);
                    state.orderCommentsReasons = payload.results.reasons;
                }
            })
            .addCase(fetchDeliveryReasons.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchDeliveryDriversByHub.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchDeliveryDriversByHub.fulfilled, (state, action) => {
                const {
                    payload,
                    meta: {
                        arg: { payload: filter }
                    }
                } = action;
                const assigneeType= filter.attributes.find((attr: any) => attr.key === "assigneeType")?.value?.[0]
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                if (assigneeType === ASSIGNEE_TYPE.DRIVER) {
                  state.driversListByHub = {
                    ...payload,
                    elements:
                      filter.pageNumber === 0
                        ? [
                            ...(Array.isArray(payload?.results?.elements)
                              ? payload.results.elements
                              : []),
                          ]
                        : [
                            ...(Array.isArray(state?.driversListByHub?.elements)
                              ? state.driversListByHub.elements
                              : []),
                            ...(Array.isArray(payload?.results?.elements)
                              ? payload.results.elements
                              : []),
                          ],
                    pageNumber: payload?.results?.pageNumber,
                    totalElements: payload?.results?.totalElements,
                    totalPages: payload?.results?.totalPages,
                  };
                }
                if (assigneeType === ASSIGNEE_TYPE.HELPER) {
                  state.helperListByHub = {
                    ...payload,
                    elements:
                      filter.pageNumber === 0
                        ? [
                            ...(Array.isArray(payload?.results?.elements)
                              ? payload.results.elements
                              : []),
                          ]
                        : [
                            ...(Array.isArray(state?.helperListByHub?.elements)
                              ? state.helperListByHub.elements
                              : []),
                            ...(Array.isArray(payload?.results?.elements)
                              ? payload.results.elements
                              : []),
                          ],
                    pageNumber: payload?.results?.pageNumber,
                    totalElements: payload?.results?.totalElements,
                    totalPages: payload?.results?.totalPages,
                  };
                }
            })
            .addCase(fetchDeliveryDriversByHub.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.error.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
                    
            })

            .addCase(expressOrderReplanning.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(expressOrderReplanning.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.replanningConsignmentSuccess = true;
                state.consignmentDataAfterReplanning = payload && payload.results;
            })
            .addCase(expressOrderReplanning.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.error.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
                state.replanningConsignmentSuccess = false;
            })
            .addCase(closeDialog.fulfilled, (state, action) => {
                state.consignmentDataAfterReplanning = {};
                state.productsAfterUpdateLineItem = [];
                state.bundleProductAfterUpdateLineItem = [];
                state.consignmentDataAfterAddressUpdate = {};
                state.isPolled = false;
                state.orderCommentsAfterSubmit = {};
                state.areOrderCommentsAdded = false;
                state.driverUpdateResult = {};
                state.supportDriverUpdateResult = {};
            })
            .addCase(resetBundleList.fulfilled, (state, action) => {
                state.bundleProductList = []
                state.crateInfo = []
                state.productList = []
            })
            .addCase(resetStatusRefreshButton.fulfilled, (state, action) => {
                state.isPolled = false
            })
            .addCase(resetExportInitiated.fulfilled, (state) => {
                state.exportInitiated = false;
            })
            .addCase(replanDuringPartialRejection.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(replanDuringPartialRejection.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.revisedAmountAfterCalculation = payload && payload.results.metadata && payload.results.metadata.collectedAmount ? payload.results.metadata.collectedAmount : "";
                state.productsAfterUpdateLineItem = payload && payload.results.products;
                state.bundleProductAfterUpdateLineItem = payload?.results?.bundleProducts?.length && transformBundleData(payload?.results?.bundleProducts)
                state.consignmentDataAfterReplanning = payload.results;
                state.replanningConsignmentSuccess = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(replanDuringPartialRejection.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.error.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
                state.replanningConsignmentSuccess = false;
            })
            .addCase(getAddressStatus.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(getAddressStatus.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.consignmentDataAfterAddressUpdate = payload && payload.results;
                state.isAddressUpdated = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(getAddressStatus.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(submitOrderComments.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(submitOrderComments.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.orderCommentsAfterSubmit = payload?.results;
                state.areOrderCommentsAdded = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(submitOrderComments.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchCarrierDropdown.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchCarrierDropdown.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.carrierList = transformSelectValues(payload || []);
            })
            .addCase(fetchCarrierDropdown.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
            })    
            .addCase(fetchClubbingSuggestion.pending, (state) => {
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchClubbingSuggestion.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.clubbingSuggestion = payload?.results;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchClubbingSuggestion.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(switchCourier.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(switchCourier.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.switchInfo = payload?.results || {};
            })
            .addCase(switchCourier.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(confirmCourierSwitch.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.courierSwitchSuccess = false;
            })
            .addCase(confirmCourierSwitch.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.courierSwitchSuccess = payload?.jobId ? true : false;
            })
            .addCase(confirmCourierSwitch.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
                state.courierSwitchSuccess = false;
            })
            .addCase(denyCourierSwitch.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
                state.courierSwitchSuccess = false;
                state.courierSwitchCanceled = false;
                state.switchInfo = {};
            })
            .addCase(denyCourierSwitch.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.courierSwitchCanceled = payload?.status == "SUCCESS" ? true : false;
            })
            .addCase(denyCourierSwitch.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
                state.courierSwitchSuccess = false;
                state.courierSwitchCanceled = false;
            })
            .addCase(resetCourierState.fulfilled, (state) => {
                state.switchInfo = {};
                state.courierSwitchSuccess = false;
                state.courierSwitchCanceled = false;
                
            })
            .addCase(clearClubbingSuggestion, (state) => {
                state.clubbingSuggestion = {};
                state.clubbingLoading = false;
            });
    },
});

export const { setClubbingAssignmentLoading, resetDeliveryBadges } = expressSlice.actions;
export default expressSlice.reducer;